import { FC, useMemo } from 'react';
import {
  Box,
  VStack,
  HStack,
  Stack,
  Text,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useColorModeValue,
  Spinner,
  Center,
  SimpleGrid,
  Flex,
  Divider,
} from '@chakra-ui/react';
import type { OverviewMetricsData, LeadTimelinePoint, FunnelBreakdown, StageTotals } from '../../types';

export interface OverviewMetricsProps {
  metrics: OverviewMetricsData | null;
  loading?: boolean;
  labels?: {
    title?: string;
    rangeAllTime?: string;
    totalLeads?: string;
    workingLeads?: string;
    convertedLeads?: string;
    discardedLeads?: string;
    conversionRate?: string;
    totalDealValue?: string;
    pipelineValue?: string;
    averageDealValue?: string;
    timelineHeading?: string;
    timelineEmpty?: string;
    funnelBreakdownTitle?: string;
    funnelBreakdownEmpty?: string;
    funnelPieTitle?: string;
    funnelPieEmpty?: string;
    stageToContact?: string;
    stageWorking?: string;
    stageConverted?: string;
    funnelColumnFunnel?: string;
    funnelColumnToContact?: string;
    funnelColumnWorking?: string;
    funnelColumnConverted?: string;
    funnelColumnTotal?: string;
  };
}

const defaultLabels = {
  title: 'Overview Metrics',
  rangeAllTime: 'All time',
  totalLeads: 'Total leads',
  workingLeads: 'Working leads',
  convertedLeads: 'Converted',
  discardedLeads: 'Discarded',
  conversionRate: 'Conversion rate',
  totalDealValue: 'Total deal value',
  pipelineValue: 'Pipeline value',
  averageDealValue: 'Average deal',
  timelineHeading: 'Lead volume over time',
  timelineEmpty: 'No timeline data yet',
  funnelBreakdownTitle: 'Funnel breakdown',
  funnelBreakdownEmpty: 'No funnels with leads yet',
  funnelPieTitle: 'Distribution by funnel',
  funnelPieEmpty: 'No leads to display',
  stageToContact: 'To contact',
  stageWorking: 'Working',
  stageConverted: 'Converted',
  funnelColumnFunnel: 'Funnel',
  funnelColumnToContact: 'To contact',
  funnelColumnWorking: 'Working',
  funnelColumnConverted: 'Converted',
  funnelColumnTotal: 'Total',
};

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const PIE_COLORS = ['#3b82f6', '#6366f1', '#ec4899', '#f97316', '#22d3ee', '#8b5cf6', '#facc15', '#10b981'];

interface ChartPoint {
  x: number;
  y: number;
  value: number;
  date: string;
}

const computeChartPoints = (
  points: LeadTimelinePoint[],
  selector: (point: LeadTimelinePoint) => number,
  width: number,
  height: number,
  paddingX: number,
  paddingY: number,
): ChartPoint[] => {
  if (points.length === 0) return [];
  const values = points.map(selector);
  const maxValue = Math.max(1, ...values);
  const innerWidth = Math.max(1, width - paddingX * 2);
  const innerHeight = Math.max(1, height - paddingY * 2);

  return points.map((point, index) => {
    const ratio = points.length === 1 ? 0.5 : index / (points.length - 1);
    const x = paddingX + ratio * innerWidth;
    const y = paddingY + innerHeight - (selector(point) / maxValue) * innerHeight;
    return { x, y, value: selector(point), date: point.date };
  });
};

const pathFromPoints = (points: ChartPoint[]) =>
  points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

export const OverviewMetrics: FC<OverviewMetricsProps> = ({
  metrics,
  loading = false,
  labels = {},
}) => {
  const mergedLabels = { ...defaultLabels, ...labels };
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const pieTrackColor = useColorModeValue('#E2E8F0', '#1F2937');

  // Precompute funnel/pie data and run hooks before any early returns
  const funnelBreakdown = (metrics?.funnel_breakdown || []) as FunnelBreakdown[];
  const fallbackStageTotals: StageTotals = funnelBreakdown.reduce<StageTotals>(
    (acc, funnel) => {
      acc.to_contact += funnel.to_contact;
      acc.working += funnel.working;
      acc.converted += funnel.converted;
      return acc;
    },
    { to_contact: 0, working: 0, converted: 0 },
  );
  const stageTotals: StageTotals = (metrics?.stage_totals as StageTotals) || fallbackStageTotals;
  const funnelTotalLeads = funnelBreakdown.reduce((sum, funnel) => sum + funnel.total_leads, 0);
  const pieSize = 220;
  const pieStrokeWidth = 32;
  const pieRadius = (pieSize - pieStrokeWidth) / 2;
  const pieCircumference = 2 * Math.PI * pieRadius;
  const pieSegments = useMemo(() => {
    if (!funnelBreakdown.length || funnelTotalLeads === 0) return [];
    let cumulative = 0;
    return funnelBreakdown.map((funnel, index) => {
      const fraction = funnel.total_leads / funnelTotalLeads;
      cumulative += fraction;
      return {
        color: PIE_COLORS[index % PIE_COLORS.length],
        dashArray: `${fraction * pieCircumference} ${pieCircumference}`,
        dashOffset: pieCircumference * (1 - cumulative),
        label: funnel.funnel_name,
        value: funnel.total_leads,
      };
    });
  }, [funnelBreakdown, funnelTotalLeads, pieCircumference]);

  if (loading) {
    return (
      <Center h="240px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!metrics) {
    return (
      <Box p={6} bg={bgColor} border="1px" borderColor={borderColor} borderRadius="lg">
        <Text color={mutedText}>{mergedLabels.timelineEmpty}</Text>
      </Box>
    );
  }

  const timeline = metrics.timeline || [];
  const chartWidth = Math.max(280, timeline.length * 80);
  const chartHeight = 220;
  const paddingX = 32;
  const paddingY = 32;

  const totalPoints = computeChartPoints(timeline, p => p.total_leads, chartWidth, chartHeight, paddingX, paddingY);
  const convertedPoints = computeChartPoints(timeline, p => p.converted_leads, chartWidth, chartHeight, paddingX, paddingY);
  const totalPath = pathFromPoints(totalPoints);
  const convertedPath = pathFromPoints(convertedPoints);
  const areaPath = totalPoints.length
    ? `${totalPath} L ${totalPoints[totalPoints.length - 1].x} ${chartHeight - paddingY} L ${totalPoints[0].x} ${chartHeight - paddingY} Z`
    : '';

  // (computed earlier before early returns)

  return (
    <VStack spacing={6} align="stretch">
      <Box p={6} bg={bgColor} border="1px" borderColor={borderColor} borderRadius="lg">
        <VStack align="stretch" spacing={4}>
          <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }}>
            <VStack align="flex-start" spacing={1}>
              <Text fontSize="xl" fontWeight="bold">
                {mergedLabels.title}
              </Text>
              {(metrics.period_start || metrics.period_end) && (
                <Text fontSize="sm" color={mutedText}>
                  {metrics.period_start && metrics.period_end
                    ? `${new Date(metrics.period_start).toLocaleDateString()} - ${new Date(metrics.period_end).toLocaleDateString()}`
                    : mergedLabels.rangeAllTime}
                </Text>
              )}
            </VStack>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Stat>
              <StatLabel>{mergedLabels.totalLeads}</StatLabel>
              <StatNumber>{metrics.total_leads}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{mergedLabels.workingLeads}</StatLabel>
              <StatNumber>{metrics.working_leads}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{mergedLabels.convertedLeads}</StatLabel>
              <StatNumber>{metrics.converted_leads}</StatNumber>
              <StatHelpText>{`${metrics.conversion_rate}%`}</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>{mergedLabels.discardedLeads}</StatLabel>
              <StatNumber>{metrics.discarded_leads}</StatNumber>
            </Stat>
          </SimpleGrid>
          <Divider />
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <Stat>
              <StatLabel>{mergedLabels.totalDealValue}</StatLabel>
              <StatNumber>{currencyFormatter.format(metrics.total_deal_value || 0)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{mergedLabels.pipelineValue}</StatLabel>
              <StatNumber>{currencyFormatter.format(metrics.pipeline_value || 0)}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>{mergedLabels.averageDealValue}</StatLabel>
              <StatNumber>{currencyFormatter.format(metrics.average_deal_value || 0)}</StatNumber>
            </Stat>
          </SimpleGrid>
        </VStack>
      </Box>

      <HStack p={6} bg={bgColor} border="1px" borderColor={borderColor} borderRadius="lg">
        <VStack align="stretch" spacing={4}>
          <Text fontSize="lg" fontWeight="semibold">
            {mergedLabels.timelineHeading}
          </Text>
          {timeline.length === 0 ? (
            <Center h="160px">
              <Text color={mutedText}>{mergedLabels.timelineEmpty}</Text>
            </Center>
          ) : (
            <Box overflowX="auto">
              <Box minW={`${chartWidth}px`}>
                <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-labelledby="crm-overview-timeline-title">
                  <title id="crm-overview-timeline-title">{mergedLabels.timelineHeading}</title>
                  <defs>
                    <linearGradient id="totalAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {areaPath && (
                    <path d={areaPath} fill="url(#totalAreaGradient)" stroke="none" />
                  )}
                  {totalPath && (
                    <path d={totalPath} fill="none" stroke="#3b82f6" strokeWidth={2} />
                  )}
                  {convertedPath && (
                    <path d={convertedPath} fill="none" stroke="#10b981" strokeWidth={2} strokeDasharray="6 6" />
                  )}
                  {totalPoints.map(point => (
                    <circle key={point.date} cx={point.x} cy={point.y} r={3.5} fill="#3b82f6" />
                  ))}
                </svg>
              </Box>
              <HStack spacing={6} mt={3} color={mutedText} fontSize="xs">
                <HStack spacing={2}>
                  <Box w="10px" h="10px" borderRadius="xs" bg="#3b82f6" />
                  <Text>{mergedLabels.totalLeads}</Text>
                </HStack>
                <HStack spacing={2}>
                  <Box w="10px" h="10px" borderRadius="xs" bg="#10b981" />
                  <Text>{mergedLabels.convertedLeads}</Text>
                </HStack>
              </HStack>
              <HStack spacing={4} mt={4} color={mutedText} fontSize="xs">
                {timeline.map(point => (
                  <VStack key={point.date} spacing={1} minW="72px" align="center">
                    <Text fontWeight="medium">{point.total_leads}</Text>
                    <Text>
                      {Number.isNaN(new Date(point.date).getTime())
                        ? point.date
                        : new Date(point.date).toLocaleDateString()}
                    </Text>
                  </VStack>
                ))}
              </HStack>
            </Box>
          )}
        </VStack>
        <Divider />
        <VStack align="stretch" spacing={4}>
          {funnelBreakdown.length === 0 ? (
            <Text color={mutedText}>{mergedLabels.funnelBreakdownEmpty}</Text>
          ) : (
            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={6}
              align="stretch"
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
            >
              <VStack align="stretch" spacing={4} w={{ base: '100%', md: '280px' }} flexShrink={0}>
                <Text fontSize="md" fontWeight="semibold">
                  {mergedLabels.funnelPieTitle}
                </Text>
                {funnelTotalLeads === 0 ? (
                  <Text color={mutedText}>{mergedLabels.funnelPieEmpty}</Text>
                ) : (
                  <>
                    <Box position="relative" w={`${pieSize}px`} h={`${pieSize}px`} mx="auto">
                      <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`} role="img" aria-labelledby="crm-overview-funnel-pie-title">
                        <title id="crm-overview-funnel-pie-title">{mergedLabels.funnelPieTitle}</title>
                        <circle
                          cx={pieSize / 2}
                          cy={pieSize / 2}
                          r={pieRadius}
                          fill="transparent"
                          stroke={pieTrackColor}
                          strokeWidth={pieStrokeWidth}
                        />
                        {pieSegments.map(segment => (
                          <circle
                            key={segment.label}
                            cx={pieSize / 2}
                            cy={pieSize / 2}
                            r={pieRadius}
                            fill="transparent"
                            stroke={segment.color}
                            strokeWidth={pieStrokeWidth}
                            strokeDasharray={segment.dashArray}
                            strokeDashoffset={segment.dashOffset}
                            transform={`rotate(-90 ${pieSize / 2} ${pieSize / 2})`}
                            strokeLinecap="round"
                          />
                        ))}
                      </svg>
                      <VStack position="absolute" inset="0" align="center" justify="center" spacing={0.5}>
                        <Text fontSize="lg" fontWeight="semibold">
                          {funnelTotalLeads}
                        </Text>
                        <Text fontSize="xs" color={mutedText}>
                          {mergedLabels.totalLeads}
                        </Text>
                      </VStack>
                    </Box>
                    <VStack align="stretch" spacing={2}>
                      {funnelBreakdown.map((funnel, index) => (
                        <Flex key={funnel.funnel_id} justify="space-between" align="center" fontSize="sm">
                          <HStack spacing={3}>
                            <Box w="10px" h="10px" borderRadius="sm" bg={PIE_COLORS[index % PIE_COLORS.length]} />
                            <Text fontWeight="medium">{funnel.funnel_name}</Text>
                          </HStack>
                          <Text fontWeight="semibold">{funnel.total_leads}</Text>
                        </Flex>
                      ))}
                    </VStack>
                  </>
                )}
              </VStack>
              <VStack align="stretch" spacing={4} flex="1" minW={0} overflowX="auto">
                <VStack align="stretch" spacing={2}>
                  <Flex
                    fontSize="xs"
                    color={mutedText}
                    textTransform="uppercase"
                    justify="space-between"
                    gap={3}
                  >
                    <Text flex="1">{mergedLabels.funnelColumnFunnel}</Text>
                    <Text w="80px" textAlign="right">
                      {mergedLabels.stageToContact}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {mergedLabels.stageWorking}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {mergedLabels.stageConverted}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {mergedLabels.funnelColumnTotal}
                    </Text>
                  </Flex>
                  {funnelBreakdown.map(funnel => (
                    <Flex key={funnel.funnel_id} justify="space-between" align="center" gap={3} fontSize="sm">
                      <Text flex="1" fontWeight="medium" noOfLines={1}>
                        {funnel.funnel_name}
                      </Text>
                      <Text w="80px" textAlign="right">
                        {funnel.to_contact}
                      </Text>
                      <Text w="80px" textAlign="right">
                        {funnel.working}
                      </Text>
                      <Text w="80px" textAlign="right">
                        {funnel.converted}
                      </Text>
                      <Text w="80px" textAlign="right" fontWeight="semibold">
                        {funnel.total_leads}
                      </Text>
                    </Flex>
                  ))}
                  <Divider />
                  <Flex justify="space-between" align="center" gap={3} fontSize="sm" fontWeight="semibold">
                    <Text flex="1">{mergedLabels.funnelColumnTotal}</Text>
                    <Text w="80px" textAlign="right">
                      {stageTotals.to_contact}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {stageTotals.working}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {stageTotals.converted}
                    </Text>
                    <Text w="80px" textAlign="right">
                      {funnelTotalLeads}
                    </Text>
                  </Flex>
                </VStack>
              </VStack>
            </Stack>
          )}
        </VStack>
      </HStack>
    </VStack>
  );
};

